import React, { useState, useEffect, useRef } from 'react';
import { Lightbulb, Play, Pause, Trash2, Music, AlertCircle, Clock, User, CirclePlus } from 'lucide-react';
import { ref, onValue, remove, push, set, update } from 'firebase/database';
import { ref as storageRef, deleteObject, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../../firebase/config';

export default function IdeasTab({ user, bandId, isPremium }) {
  const [ideas, setIdeas] = useState([]);
  const [songs, setSongs] = useState([]);
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [showAddToSong, setShowAddToSong] = useState(null);
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [converting, setConverting] = useState(false);
  const audioRef = useRef(null);

  // Free tier limit
  const FREE_IDEAS_LIMIT = 3;
  const activeIdeasCount = ideas.filter(i => i.status !== 'converted').length;
  const canAddIdea = isPremium || activeIdeasCount < FREE_IDEAS_LIMIT;

  // Listen to ideas
  useEffect(() => {
    if (!user || !bandId) {
      setIdeas([]);
      return;
    }

    const ideasRef = ref(database, `bands/${bandId}/ideas`);
    
    const unsubscribe = onValue(ideasRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ideasArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setIdeas(ideasArray);
      } else {
        setIdeas([]);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Listen to songs (for "Add to Song" feature)
  useEffect(() => {
    if (!user || !bandId) {
      setSongs([]);
      return;
    }

    const songsRef = ref(database, `bands/${bandId}/songs`);
    
    const unsubscribe = onValue(songsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const songsArray = Object.keys(data).map(key => ({
          id: key,
          name: data[key].name
        })).sort((a, b) => a.name.localeCompare(b.name));
        setSongs(songsArray);
      } else {
        setSongs([]);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Handle audio playback end
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.onended = () => setPlayingId(null);
    }
  }, [playingId]);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  const togglePlayback = (idea) => {
    if (playingId === idea.id) {
      // Stop current
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingId(null);
    } else {
      // Play new
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingId(idea.id);
      
      // Small delay to let state update
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = idea.fileURL;
          audioRef.current.play().catch(err => {
            console.error('Playback failed:', err);
            setPlayingId(null);
          });
        }
      }, 50);
    }
  };

  const deleteIdea = async (idea) => {
    if (!user || !bandId) return;
    if (!window.confirm(`Delete "${idea.label}"?`)) return;

    try {
      // Stop playback if this idea is playing
      if (playingId === idea.id) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setPlayingId(null);
      }

      // Delete from storage
      if (idea.storagePath) {
        try {
          const fileRef = storageRef(storage, idea.storagePath);
          await deleteObject(fileRef);
        } catch (err) {
          console.log('File not found in storage, continuing');
        }
      }

      // Delete from database
      const ideaRef = ref(database, `bands/${bandId}/ideas/${idea.id}`);
      await remove(ideaRef);
      
      setError('');
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    }
  };

  const addToSong = async (idea) => {
    if (!selectedSongId || !user || !bandId) return;

    try {
      setConverting(true);
      setError('');

      // Fetch the audio file and re-upload to the song's audio folder
      const response = await fetch(idea.fileURL);
      const blob = await response.blob();

      const timestamp = Date.now();
      const extension = idea.fileName.split('.').pop() || 'webm';
      const fileName = `version-${timestamp}.${extension}`;
      const storagePath = `bands/${bandId}/audio/${selectedSongId}/${fileName}`;

      // Upload to new location
      const fileRef = storageRef(storage, storagePath);
      const uploadTask = uploadBytesResumable(fileRef, blob);

      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          null,
          (error) => reject(error),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      // Add as version to the song
      const versionRef = ref(database, `bands/${bandId}/songs/${selectedSongId}/versions`);
      const newVersionRef = push(versionRef);
      
      await set(newVersionRef, {
        fileName,
        fileURL: downloadURL,
        storagePath,
        uploadDate: new Date().toISOString(),
        fileSize: (blob.size / 1024).toFixed(2),
        uploadedBy: user.uid,
        uploadedByName: user.displayName || user.email,
        notes: `From idea: ${idea.label}`
      });

      // Update song's updatedAt
      const songRef = ref(database, `bands/${bandId}/songs/${selectedSongId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });

      // Mark idea as converted (or delete it)
      const ideaRef = ref(database, `bands/${bandId}/ideas/${idea.id}`);
      await update(ideaRef, { 
        status: 'converted',
        convertedToSong: selectedSongId,
        convertedAt: new Date().toISOString()
      });

      setConverting(false);
      setShowAddToSong(null);
      setSelectedSongId(null);
    } catch (err) {
      console.error('Failed to add to song:', err);
      setError('Failed to convert: ' + err.message);
      setConverting(false);
    }
  };

  // Filter out converted ideas (or show them differently)
  const activeIdeas = ideas.filter(i => i.status !== 'converted');
  const convertedIdeas = ideas.filter(i => i.status === 'converted');

  return (
    <div className="space-y-6">
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} />

      {error && (
        <div className="bg-red-900/50 rounded-card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-red-100 text-sm">{error}</p></div>
          <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
        </div>
      )}

    {/* Info Box */}
      <div className="card-3d rounded-card p-4">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-1">Quick Capture</p>
            <p className="text-sm text-text-subtle">
              Record ideas on the fly and add them to your songs later. 
              Use the Record button to capture new ideas.
            </p>
            {!isPremium && (
              <p className="text-xs text-text-subtle mt-2">
                {activeIdeasCount}/{FREE_IDEAS_LIMIT} ideas used (free tier)
              </p>
            )}
          </div>
        </div>
      </div>

      {!canAddIdea && (
        <div className="bg-yellow-900/50 rounded-card px-4 py-3">
          <p className="text-yellow-200 text-sm">
            You've reached the free limit of {FREE_IDEAS_LIMIT} ideas. Convert ideas to songs or upgrade to Premium for unlimited ideas.
          </p>
        </div>
      )}

      {/* Active Ideas */}
      {activeIdeas.length === 0 ? (
        <div className="card-3d rounded-card p-8 text-center text-text-subtle">
          <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No ideas yet</p>
          <p className="text-sm mt-1">Use the Record button to capture your first idea!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeIdeas.map(idea => (
            <div key={idea.id} className="card-3d rounded-card p-4">
              <div className="flex items-start gap-3">
                {/* Play Button */}
                <button
                  onClick={() => togglePlayback(idea)}
                  className={`p-3 rounded-full flex-shrink-0 transition border ${
                    playingId === idea.id
                      ? 'bg-brand border-transparent text-white'
                      : 'bg-transparent border-surface-border-light hover:border-surface-border text-white'
                  }`}
                >
                  {playingId === idea.id ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5" />
                  )}
                </button>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{idea.label}</p>
                  <div className="flex items-center gap-3 text-xs text-text-subtle mt-1">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDuration(idea.duration)}
                    </span>
                    <span>{formatTimeAgo(idea.createdAt)}</span>
                    {idea.createdByName && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {idea.createdByName}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => {
                      setShowAddToSong(idea.id);
                      setSelectedSongId(null);
                    }}
                    className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                    
                    title="Add to Song"
                  >
                    <CirclePlus className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => deleteIdea(idea)}
                    className="bg-transparent hover:bg-red-600 transition p-2 rounded-full"
                    
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>

              {/* Add to Song Modal */}
              {showAddToSong === idea.id && (
                <div className="mt-4 pt-4 border-t border-surface-border-light">
                  <p className="text-sm font-semibold mb-3">Add to Song</p>
                  
                  {songs.length === 0 ? (
                    <p className="text-text-subtle text-sm">
                      No songs yet. Create a song first in the Songs tab.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                      {songs.map(song => (
                        <button
                          key={song.id}
                          onClick={() => setSelectedSongId(song.id)}
                          className={`w-full text-left px-3 py-2 rounded transition flex items-center gap-2 ${
                            selectedSongId === song.id
                              ? 'bg-brand text-white rounded-card-btn'
                              : 'bg-transparent border border-surface-border-light hover:border-surface-border text-white rounded-card-btn'
                          }`}
                        >
                          <Music className="w-4 h-4" />
                          {song.name}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowAddToSong(null);
                        setSelectedSongId(null);
                      }}
                      className="flex-1 bg-transparent border border-surface-border-light hover:border-surface-border transition px-4 py-2 rounded-pill 
                        font-semibold text-sm"
                      
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => addToSong(idea)}
                      disabled={!selectedSongId || converting}
                      className="flex-1 bg-brand hover:bg-brand-hover 
                        disabled:opacity-disabled disabled:cursor-not-allowed 
                        transition px-4 py-2 font-semibold text-sm rounded-pill"
                      
                    >
                      {converting ? 'Adding...' : 'Add as Version'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Converted Ideas (collapsed section) */}
      {convertedIdeas.length > 0 && (
        <div className="border-t border-surface-border-light pt-6">
          <p className="text-sm text-text-subtle mb-3">
            Converted to songs ({convertedIdeas.length})
          </p>
          <div className="space-y-2">
            {convertedIdeas.map(idea => (
              <div key={idea.id} className="card-3d rounded-card p-3 
                flex items-center justify-between opacity-60">
                <div className="flex items-center gap-3">
                  <Lightbulb className="w-4 h-4 text-text-subtle" />
                  <span className="text-sm text-text-subtle">{idea.label}</span>
                </div>
                <button
                  onClick={() => deleteIdea(idea)}
                  className="text-text-subtle hover:text-red-400 transition p-1"
                  title="Delete"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
